<template>
  <div class="rounded-lg select-none flex cursor-grab relative" :style="state.messageStyle" :class="state.messageClass">
    <div
      v-if="hasAttachment || props.note.tags.length > 0 || hasEntryAndIsNotPhone"
      class="flex self-end bg-gray-800 absolute rounded-tr-lg rounded-bl-lg"
    >
      <!-- attachment icons -->
      <div v-if="hasAttachment" class="flex items-center justify-center">
        <div v-if="props.note.attachment?.type === AttachmentType.image" class="w-8 h-8 flex self-center rounded-3xl p-2">
          <img class="opacity-50 invert" :src="Image" :alt="t('aria-status-attachment')" />
        </div>
        <div v-else-if="props.note.attachment?.type === AttachmentType.external" class="w-8 h-8 flex self-center ml-2 rounded-3xl p-2">
          <img class="opacity-50 invert" :src="Link" :alt="t('aria-status-link')" />
        </div>
        <div v-if="props.note.tags.length > 0 && screensizeType" class="w-8 h-8 mr-px flex self-center ml-2 rounded-3xl p-2">
          <img class="opacity-50 invert" :src="Tag" :alt="t('aria-status-tag')" />
        </div>
      </div>
      <div v-if="hasEntryAndIsNotPhone" class="w-8 h-8 flex self-center justify-center p-2 rounded-full ml-1">
        <img class="opacity-50 invert" :src="Pin" :alt="t('aria-status-pin')" />
      </div>
    </div>
    <div class="flex flex-row justify-between p-2">
      <!--- note content -->
      <div class="flex flex-col w-full">
        <div class="flex w-full justify-between mb-1" :class="wrapperStyles">
          <div class="flex text-base items-baseline">
            <div class="rounded-lg" :style="`background-color: ${color.actions.selectColor(props.note.status.createdBy.username).background};`">
              <p class="text-left mx-1" :style="`color: ${color.actions.selectColor(props.note.status.createdBy.username).text};`">
                {{ props.note.status.createdBy.username }}
              </p>
            </div>
            <p class="flex items-center justify-center ml-2 text-sm" :class="state.textClass">
              {{ common.actions.getCleanTimestamp(props.note.status.created) }}
            </p>
          </div>
        </div>
        <div class="flex my-1 flex-col">
          <p v-if="!state.noteEditIsVisible" class="w-full text-left font-thin text-lg mt-1 break-words whitespace-pre-wrap">
            {{ extractTags(props.note.text) }}
          </p>
          <textarea
            v-else
            ref="textarea"
            v-model="state.noteText"
            autofocus
            class="w-full bg-black text-left text-lg mt-1 p-2 rounded-xl break-words resize-none overflow-hidden min-h-32"
            @keydown.shift.enter.prevent.stop="toggleNoteEditVisibility()"
            @touchstart.prevent="focusTextarea()"
            @click.left.prevent="focusTextarea()"
            @input="autoResize"
          ></textarea>
          <p v-if="state.noteEditIsVisible" class="w-full text-right text-xs opacity-60 mt-1 pr-1 text-gray-50">{{ t('shift-enter-confirm') }}</p>
        </div>
        <div v-if="!props.note.entries[props.wallId]" class="flex flex-wrap">
          <!-- tags and links -->
          <div v-for="(tag, index) in props.note.tags" :key="index" class="text-sm soft-btn-nh rounded-2xl px-2 py-px mr-2 mt-2 cursor-none">
            {{ tag }}
          </div>
          <div
            v-for="(url, index) in actualUrls"
            :key="index"
            class="text-sm text-white bg-slate-middle rounded-2xl px-2 py-1 mr-2 mt-2 cursor-none flex items-center"
          >
            <img class="invert w-4 mr-1.5" :src="Link" />
            <p>{{ common.actions.cleanUrlString(url) }}</p>
          </div>
        </div>
      </div>
    </div>
    <div class="flex justify-end mb-2 mr-2" v-if="!props.locked">
      <!-- edit panel — normal icons when not editing -->
      <div v-if="!state.noteEditIsVisible" :id="`wrapper-${props.note._id}`" class="flex">
        <div
          class="w-9 h-9 rounded-full bg-slate-middle p-2 cursor-pointer"
          tabindex="0"
          @pointerdown.prevent.stop="toggleNoteEditVisibility()"
          @keydown.enter.prevent.stop="toggleNoteEditVisibility()"
        >
          <img class="invert w-5 p-1" :src="Edit" :alt="t('aria-edit-note')" />
        </div>
        <div
          v-if="deleteActionAllowed"
          tabindex="0"
          class="ml-2 w-9 h-9 flex justify-center bg-slate-middle items-center cursor-pointer rounded-full"
          @pointerdown.prevent.stop="deleteNote()"
          @keydown.enter.prevent.stop="deleteNote()"
        >
          <img :src="Close" class="w-5 h-5 invert rotate-45" :alt="t('aria-remove-note')" />
        </div>
      </div>

      <!-- confirm button — only shown while editing -->
      <div v-else class="flex items-center justify-end mr-1">
        <div
          class="rounded-full bg-slate-middle cursor-pointer px-2 h-8 flex justify-center items-center"
          :title="t('shift-enter-confirm')"
          tabindex="0"
          @pointerdown.prevent.stop="toggleNoteEditVisibility()"
          @keydown.enter.prevent.stop="toggleNoteEditVisibility()"
        >
          <!--img class="invert w-8 h-8" :src="CircleCheck" :alt="t('aria-edit-note')" /-->
          <span class="text-xs">{{ t('done') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onMounted, reactive, watch, computed, ref, nextTick } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { DialogMessageType, Note, Parcel } from '@/common/classes'
  import { ScreensizeType, AttachmentType, ParcelType, PermissionLevel } from '@/constants'
  import { StoreResponse } from '@/common/interfaces'

  import useColorStore from '@/composables/colors'
  import useCommonStore from '@/composables/common'
  import useDialogStore from '@/composables/dialog'
  import useParcelStore from '@/composables/parcel'

  import { useUserStore } from '@/composables/userStore'
  import { useNoteStore } from '@/composables/noteStore'
  import useWallStore from '@/composables/wallStore'

  import CircleCheck from '@/assets/icons/fontawesome/circle-check-solid.svg'
  import Edit from '@/assets/icons/fontawesome/pen-solid.svg'
  import Image from '@/assets/icons/fontawesome/image-solid.svg'
  import Link from '@/assets/icons/fontawesome/link-simple-regular.svg'
  import Tag from '@/assets/icons/fontawesome/tag-solid.svg'
  import Close from '@/assets/icons/fontawesome/plus-solid.svg'
  import Pin from '@/assets/icons/fontawesome/thumbtack-sharp-solid.svg'

  const color = useColorStore()
  const common = useCommonStore()
  const userStore = useUserStore()
  const noteStore = useNoteStore()
  const wallStore = useWallStore()
  const dialogStore = useDialogStore()
  const parcelStore = useParcelStore()

  const textarea = ref<HTMLElement | null>(null)

  const emit = defineEmits<{
    (e: 'deleteNote', note: Note): void
    (e: 'editAction', value: boolean): void
  }>()

  const { t } = useI18n()

  const { screensizeType } = common.getters

  const props = defineProps({
    note: { type: Note, required: true },
    wallId: { type: String, default: '' },
    locked: { type: Boolean, default: false },
  })

  const state = reactive({
    messageStyle: '',
    messageClass: 'bg-slate-bright border-white-dark text-slate-white',

    textClass: 'text-slate-white',
    noteText: '',

    noteEditIsVisible: false,
  })

  onMounted(() => {
    hideByEntryState()
  })

  watch(props.note, () => {
    hideByEntryState()
  })

  //Must also watch props.note.text for the sidebar note to pick up the new text state in sidebar before loading the text into the editor.
  watch(
    () => props.note.text,
    (newText) => {
      state.noteText = newText
    },
  )

  const hasEntryAndIsNotPhone = computed(() => {
    return props.note.entries[props.wallId] !== undefined && common.getters.screensizeType.value !== ScreensizeType.phone
  })

  const hasAttachment = computed(() => {
    return [AttachmentType.image, AttachmentType.external].some((type) => type === props.note.attachment?.type)
  })

  const wrapperStyles = computed(() => {
    return screensizeType.value === ScreensizeType.phone ? 'items-start' : 'items-baseline'
  })

  const dragActionAllowed = computed(() => {
    return props.note.entries === undefined || props.note.entries[props.wallId] === undefined
  })

  const deleteActionAllowed = computed(() => {
    const wall = wallStore.getters.selectedWall.value
    const user = userStore.getters.user.value
    if (user && wall) {
      return props.note.status.createdBy.user_id === user._id || common.actions.hasMinimumPermission(user, PermissionLevel.Organize, wall)
    } else return false
  })

  function extractTags(text: string) {
    for (const url of actualUrls.value) text = text.replace(url, '')
    return text
  }

  function hideByEntryState() {
    // UI state of the note in the sidebar upon retrieval / initial state
    if (dragActionAllowed.value) state.messageStyle = 'opacity: 1; cursor: grab;'
    else state.messageClass += ` pointer-events-none`

    state.messageStyle += ` flex-direction: column;`
    state.messageClass = 'bg-slate-bright'

    state.noteText = props.note.text
    emit('editAction', false)
  }

  const actualUrls = computed(() => common.actions.getActualUrlsFromText(props.note.text))

  function toggleNoteEditVisibility() {
    emit('editAction', !state.noteEditIsVisible)
    state.noteEditIsVisible = !state.noteEditIsVisible
    if (state.noteEditIsVisible) {
      nextTick(() => {
        if (textarea.value) {
          const el = textarea.value as HTMLTextAreaElement
          el.style.height = 'auto'
          el.style.height = el.scrollHeight + 'px'
        }
      })
    } else {
      validateNewText(false)
    }
  }

  function focusTextarea() {
    if (textarea.value) textarea.value.focus()
  }

  function autoResize(event: Event) {
    const el = event.target as HTMLTextAreaElement
    el.style.height = 'auto'
    el.style.height = el.scrollHeight + 'px'
  }

  async function validateNewText(toggle = true) {
    const updatedNote = new Note(props.note)
    const wall = wallStore.getters.selectedWall.value
    if (wall) {
      let creator = userStore.getters.getUserAsCreator.value
      if (!creator) {
        const res = await userStore.actions.getUserDetails()
        if (res.success) creator = userStore.getters.getUserAsCreator.value
        else return dialogStore.actions.pushMessage('', DialogMessageType.Error, t('creatorfail'))
      } else {
        updatedNote.addEdit(state.noteText, creator)
        const res = await noteStore.actions.updateNote(updatedNote, wall._id, ParcelType.MessageEdited)
        if (res.success) {
          const parcel = new Parcel({ parcelType: ParcelType.MessageEdited, body: updatedNote }, userStore.getters.user.value, wall)
          await parcelStore.actions.sendParcel(wall._id, parcel)
          if (toggle) toggleNoteEditVisibility()
        }
      }
    }
  }

  function deleteNote() {
    emit('deleteNote', props.note)
  }
</script>

<style scoped></style>
